const PUSH_PUBLIC_KEY_ENDPOINT = '/api/push/public-key';
const PUSH_SUBSCRIBE_ENDPOINT = '/api/push/subscribe';
const PUSH_UNSUBSCRIBE_ENDPOINT = '/api/push/unsubscribe';
const PUSH_REMINDER_SETTINGS_ENDPOINT = '/api/push/reminder-settings';
const PUSH_ACTIVITY_ENDPOINT = '/api/push/activity';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);

  return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
}

async function fetchJson(url, options) {
  const response = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...(options?.headers || {}),
    },
    ...options,
  });

  const contentType = response.headers.get('content-type') || '';
  const data = await response.json().catch(() => ({}));
  const isJsonResponse = contentType.includes('application/json');

  if (!isJsonResponse) {
    throw new Error('Push API returned an invalid response. Redeploy the app and try again.');
  }

  if (!response.ok) {
    throw new Error(data.error || 'Request failed');
  }

  return data;
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function getPushRegistration() {
  return navigator.serviceWorker.ready;
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) {
    return null;
  }

  const registration = await getPushRegistration();
  return registration.pushManager.getSubscription();
}

export async function getPushState() {
  if (!isPushSupported()) {
    return {
      supported: false,
      permission: 'unsupported',
      subscribed: false,
    };
  }

  const subscription = await getCurrentSubscription();

  return {
    supported: true,
    permission: Notification.permission,
    subscribed: Boolean(subscription),
  };
}

export async function subscribeToPush() {
  if (!isPushSupported()) {
    throw new Error('Daily reminders are not supported on this device.');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Daily reminder permission was not granted.');
  }

  const registration = await getPushRegistration();
  const existingSubscription = await registration.pushManager.getSubscription();
  if (existingSubscription) {
    await fetchJson(PUSH_SUBSCRIBE_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({ subscription: existingSubscription }),
    });
    return existingSubscription;
  }

  const { publicKey } = await fetchJson(PUSH_PUBLIC_KEY_ENDPOINT);
  if (!publicKey) {
    throw new Error('Push public key is missing from the server response.');
  }
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });

  await fetchJson(PUSH_SUBSCRIBE_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({ subscription }),
  });

  return subscription;
}

export async function unsubscribeFromPush() {
  const subscription = await getCurrentSubscription();
  if (!subscription) {
    return false;
  }

  await fetchJson(PUSH_UNSUBSCRIBE_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({ subscription }),
  });

  await subscription.unsubscribe();
  return true;
}

export async function getReminderSettings() {
  const subscription = await getCurrentSubscription();
  if (!subscription) {
    throw new Error('Enable daily reminder first.');
  }

  const { settings } = await fetchJson(PUSH_REMINDER_SETTINGS_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({
      mode: 'get',
      subscription,
    }),
  });

  return settings;
}

export async function saveReminderSettings({ reminderEnabled, reminderTime, timezone }) {
  const subscription = await getCurrentSubscription();
  if (!subscription) {
    throw new Error('Enable daily reminder first.');
  }

  const { settings } = await fetchJson(PUSH_REMINDER_SETTINGS_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({
      subscription,
      reminderEnabled,
      reminderTime,
      timezone,
    }),
  });

  return settings;
}

export async function syncPushStudyActivity(lastStudyDate) {
  const subscription = await getCurrentSubscription();
  if (!subscription || !lastStudyDate) {
    return null;
  }

  return fetchJson(PUSH_ACTIVITY_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({
      subscription,
      lastStudyDate,
    }),
  });
}
